import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { api } from '../hooks/useAuth';

export default function ManualConnect() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [wooStatus, setWooStatus] = useState(null);

  useEffect(() => {
    fetchWooStatus();
  }, []);

  const fetchWooStatus = async () => {
    try {
      const { data } = await api.get('/api/woocommerce/status');
      setWooStatus(data);
    } catch (_) {}
  };

  const manualConnectInstagram = async (e) => {
    e.preventDefault();
    setLoading(true);
    const form = e.target;
    
    try {
      await api.post('/instagram/manual-connect', {
        instagramBusinessId: form.instagramId.value,
        pageAccessToken: form.accessToken.value,
      });

      setMessage({ type: 'success', text: '✅ Instagram connected! Syncing reels...' });
      
      // Auto-sync reels
      try {
        const { data } = await api.get('/api/auth/facebook/reels');
        if (data.reels && data.reels.length > 0) {
          await api.post('/api/auth/facebook/reels/save', { reels: data.reels });
          setMessage({ type: 'success', text: `✅ Success! ${data.reels.length} reels synced.` });
        } else {
          setMessage({ type: 'info', text: '✅ Connected, but no reels found.' });
        }
      } catch (_) {
        setMessage({ type: 'success', text: '✅ Connected! Go to Reels page to sync.' });
      }
      
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Invalid credentials' });
    } finally {
      setLoading(false);
    }
  };

  const connectWooCommerce = async (e) => {
    e.preventDefault();
    setLoading(true);
    const form = e.target;
    
    try {
      await api.post('/api/woocommerce/connect', {
        storeName: form.storeName.value,
        consumerKey: form.consumerKey.value,
        consumerSecret: form.consumerSecret.value,
      });
      setMessage({ type: 'success', text: '✅ WooCommerce connected! You can now search products.' });
      fetchWooStatus();
      form.reset();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Connection failed. Check your credentials.' });
    } finally {
      setLoading(false);
    }
  };

  const connectShopify = async (e) => {
    e.preventDefault();
    setLoading(true);
    const form = e.target;
    
    try {
      await api.post('/api/auth/shopify/manual_shopify_connect', {
        shopName: form.shopName.value,
        apiKey: form.apiKey.value,
        password: form.password.value,
        sharedSecret: form.sharedSecret.value,
      });
      setMessage({ type: 'success', text: '✅ Shopify connected! You can now search products.' });
      form.reset();
    } catch (e) {
      setMessage({ type: 'error', text: 'Connection failed. Check your credentials.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">⚙️ Manual Connect</h1>
            <p className="page-subtitle">Enter credentials directly (for advanced users)</p>
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: 24 }}>
            {message.text}
          </div>
        )}

        {/* Instagram Manual Connect */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Instagram Business Account</h3>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>
            Enter your Instagram Business ID and Page Access Token
          </p>

          <form onSubmit={manualConnectInstagram}>
            <div className="form-group">
              <label className="form-label">Instagram Business Account ID</label>
              <input 
                name="instagramId" 
                className="form-input" 
                placeholder="17841..." 
                required 
              />
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
                📍 Find in: Meta Business Suite → Settings → Instagram Accounts
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Long-Lived Page Access Token</label>
              <textarea 
                name="accessToken" 
                className="form-input" 
                rows="3"
                placeholder="EAAW..." 
                required 
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
                📍 Generate from: Graph API Explorer → Select App → Add Permissions → Generate Token → Extend Token
              </p>
            </div>

            <button className="btn btn-primary btn-lg" type="submit" disabled={loading}>
              {loading ? <><span className="spinner" /> Loading Data...</> : '📥 Load Data & Sync Reels'}
            </button>
          </form>
        </div>

        {/* WooCommerce - Main Feature Here */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>WooCommerce Store</h3>
              <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                Connect your WooCommerce store to tag products in reels
              </p>
            </div>
            {wooStatus?.connected && (
              <span className="badge badge-success">✓ Connected</span>
            )}
          </div>

          {wooStatus?.connected && (
            <div style={{ 
              padding: '12px 16px', 
              background: 'rgba(6, 214, 160, 0.1)', 
              border: '1px solid rgba(6, 214, 160, 0.3)',
              borderRadius: 10,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <span style={{ fontSize: 20 }}>✓</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
                  Connected to WooCommerce
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>
                  Store: {wooStatus.store_url}
                </div>
              </div>
              <a href="/reels" className="btn btn-secondary btn-sm">
                Go Tag Products →
              </a>
            </div>
          )}

          <form onSubmit={connectWooCommerce}>
            <div className="form-group">
              <label className="form-label">Store URL</label>
              <input 
                name="storeName" 
                className="form-input" 
                placeholder="https://yourstore.com" 
                defaultValue={wooStatus?.store_url || ''}
                required 
              />
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
                Your WooCommerce store URL (with https://)
              </p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Consumer Key</label>
                <input 
                  name="consumerKey" 
                  className="form-input" 
                  placeholder="ck_..." 
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Consumer Secret</label>
                <input 
                  name="consumerSecret" 
                  className="form-input" 
                  type="password" 
                  placeholder="cs_..." 
                  required 
                />
              </div>
            </div>
            
            <div style={{ 
              padding: '14px 16px', 
              background: 'var(--bg)', 
              borderRadius: 10, 
              marginBottom: 16,
              fontSize: 12,
              lineHeight: 1.6
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>📚 How to get WooCommerce API keys:</div>
              <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--text-dim)' }}>
                <li>Login to WordPress admin</li>
                <li>Go to: <strong>WooCommerce → Settings → Advanced → REST API</strong></li>
                <li>Click: <strong>Add key</strong></li>
                <li>Description: "VidsCommerce"</li>
                <li>Permissions: <strong>Read/Write</strong></li>
                <li>Click: <strong>Generate API key</strong></li>
                <li>Copy Consumer Key and Consumer Secret</li>
              </ol>
            </div>

            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? <><span className="spinner" /> Connecting...</> : 'Connect WooCommerce'}
            </button>
          </form>
        </div>

        {/* Shopify - Optional */}
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Shopify Store (Optional)</h3>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>
            Alternative to WooCommerce - connect your Shopify store
          </p>

          <form onSubmit={connectShopify}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Store Name</label>
                <input name="shopName" className="form-input" placeholder="yourstore.myshopify.com" required />
              </div>
              <div className="form-group">
                <label className="form-label">API Key</label>
                <input name="apiKey" className="form-input" placeholder="API Key" required />
              </div>
              <div className="form-group">
                <label className="form-label">Admin API Token</label>
                <input name="password" className="form-input" type="password" placeholder="shpat_..." required />
              </div>
              <div className="form-group">
                <label className="form-label">Shared Secret</label>
                <input name="sharedSecret" className="form-input" type="password" placeholder="Secret" required />
              </div>
            </div>
            <button className="btn btn-secondary" type="submit" disabled={loading}>
              {loading ? <><span className="spinner" /> Connecting...</> : 'Connect Shopify'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}